import { useRef, useState } from 'react'
import { DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons'

import { Button, Flex, Paragraph, Text } from 'components/uiKit'
import { filesValidate } from 'utils/helpers/files/filesValidate'
import { ConvertedFile, handleFileInput } from 'utils/helpers/files/fileUpload'

import { Margin } from '../Margin/Margin'
import { Spinner } from '../Spinner/Spinner'

import s from './UploadFile.module.scss'

type UploadFileProps = {
  contentUrl: string
  onChange?: (file: ConvertedFile) => void
  onDelete?: () => void
  isFileUploading?: boolean
  label?: string
  description?: string[] | string
  hidePreview?: boolean
  isPublishVisible?: boolean
  disabled?: boolean
  fileValidationOptions?: {
    maxFileSizeMb?: number
    allowedFileFormats?: string[]
  }
  dataTestId?: string
}

export const UploadFile = ({
  contentUrl,
  label,
  description,
  disabled = false,
  fileValidationOptions = { maxFileSizeMb: 200 },
  hidePreview = false,
  dataTestId,
  isFileUploading,
  onChange,
  onDelete,
}: UploadFileProps) => {
  const [loading, setLoading] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const isDisabled = disabled || isFileUploading || loading

  const changeFileHandler = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) {
      return
    }

    if (fileValidationOptions) {
      if (filesValidate(event.target.files, { maxFileSizeMb: 200, ...fileValidationOptions })) {
        loadFile(event)
      } else {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
    } else {
      loadFile(event)
    }
  }

  const loadFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true)
    const file = await handleFileInput(event)
    setLoading(false)

    onChange?.(file)
  }

  const deleteButtonHandler = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    onDelete?.()
  }

  return (
    <Flex align="start" gap={24}>
      <div className={s.fileInputWrap}>
        {contentUrl && !isDisabled && (
          <Button className={s.deleteButton} onClick={deleteButtonHandler} icon={<DeleteOutlined />} shape="circle" />
        )}

        <label>
          <div
            className={s.upload}
            style={{
              backgroundImage: hidePreview || isFileUploading ? undefined : `url(${contentUrl})`,
            }}
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.value = ''
              }
            }}
          >
            {loading || isFileUploading ? (
              <Spinner />
            ) : (
              !contentUrl && !isDisabled && <PlusCircleOutlined className={s.plusIcon} />
            )}
          </div>

          <input
            type="file"
            onChange={(event) => changeFileHandler(event)}
            style={{ display: 'none' }}
            ref={fileInputRef}
            disabled={isDisabled}
            data-testid={dataTestId}
          />
        </label>
      </div>

      {(label || description) && (
        <Flex vertical>
          <Margin mb={8}>
            <Text strong>{label}</Text>
          </Margin>

          {Array.isArray(description) ? (
            description.map((item) => <Paragraph key={item}>{item}</Paragraph>)
          ) : (
            <Paragraph>{description}</Paragraph>
          )}
        </Flex>
      )}
    </Flex>
  )
}
